import { useMemo, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import {
  exportToExcel,
  type ExcelProjectSummary,
  type ExportLocale,
} from '@/features/reports/utils/exportToExcel';
import type { ColumnKey } from '@/features/reports/components/ColumnSelector';
import type { ReportTask, ReportFilters } from '@/features/reports/utils/exportToExcel';
import { captureEvent } from '@/analytics/posthog';

interface ExportButtonProps {
  entries: readonly ReportTask[];
  columns: ColumnKey[];
  filters: ReportFilters;
  locale: ExportLocale;
  onLocaleChange: (locale: ExportLocale) => void;
}

// Locale options shown in the popover dropdown. Labels are static (always
// English — matches the system default). The selected value drives only
// the Excel content (column headers, date format) — not the popover UI.
const LOCALE_OPTIONS: Array<{ value: ExportLocale; label: string; flag: string }> = [
  { value: 'en-US', label: 'English', flag: '🇺🇸' },
  { value: 'pt-BR', label: 'Português', flag: '🇧🇷' },
];

export function ExportButton({
  entries,
  columns,
  filters,
  locale,
  onLocaleChange,
}: ExportButtonProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);

  const availableProjects = useMemo<ExcelProjectSummary[]>(() => {
    const map = new Map<string, ExcelProjectSummary>();
    for (const entry of entries) {
      // Project-less entries are still exported. They simply do not have a
      // project to expose in the optional project summary selector.
      if (!entry.project) continue;
      if (!map.has(entry.project.id)) {
        map.set(entry.project.id, {
          id: entry.project.id,
          name: entry.project.name,
          color: entry.project.color,
        });
      }
    }
    return Array.from(map.values());
  }, [entries]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(availableProjects.map((p) => p.id)),
  );

  // Keep selection in sync when entries change (e.g., user changes web filter)
  const projectKey = useMemo(
    () => availableProjects.map((p) => p.id).sort().join('|'),
    [availableProjects],
  );
  const [lastProjectKey, setLastProjectKey] = useState(projectKey);
  if (projectKey !== lastProjectKey) {
    setLastProjectKey(projectKey);
    setSelectedIds(new Set(availableProjects.map((p) => p.id)));
  }

  const toggleProject = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(availableProjects.map((p) => p.id)));
  const clearAll = () => setSelectedIds(new Set());

  const handleExport = async () => {
    if (entries.length === 0) return;
    await exportToExcel([...entries], columns, filters, {
      locale,
      selectedProjectIds: Array.from(selectedIds),
      availableProjects,
    });
    captureEvent('report_exported', {
      format: 'xlsx',
      entry_count: entries.length,
    });
    setPopoverOpen(false);
  };

  const selectedCount = selectedIds.size;
  const totalCount = availableProjects.length;
  const canExport = entries.length > 0;

  const currentLocaleLabel =
    LOCALE_OPTIONS.find((opt) => opt.value === locale)?.label ?? locale;

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={!canExport}
          title="Open export options"
          data-testid="export-excel-button"
        >
          <Download className="mr-2 h-4 w-4" />
          Export Excel
          {availableProjects.length > 0 && (
            <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {selectedCount}/{totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="end"
        data-testid="export-excel-popover"
      >
        <div className="space-y-3 p-3">
          <div className="space-y-1">
            <Label className="text-xs">Excel language</Label>
            <Select
              value={locale}
              onValueChange={(v) => onLocaleChange(v as ExportLocale)}
            >
              <SelectTrigger
                className="h-8 text-xs"
                data-testid="export-locale-select"
              >
                <SelectValue>
                  {LOCALE_OPTIONS.find((opt) => opt.value === locale)?.flag}{' '}
                  {currentLocaleLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-[110]">
                {LOCALE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.flag} {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-4 flex">
              Affects only the Excel content (column language + date
              format). The modal stays in English.
            </p>
          </div>

          {availableProjects.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="export-no-projects-message">
              No projects available. Tasks without a project will still be exported.
            </p>
          ) : (
            <Popover
              open={projectsOpen}
              onOpenChange={setProjectsOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between text-xs font-normal"
                  data-testid="export-projects-button"
                >
                  <span>
                    Selected projects ({selectedCount}/{totalCount})
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      projectsOpen ? 'rotate-180' : ''
                    }`}
                  />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-72 p-0"
                align="start"
                side="bottom"
                data-testid="export-projects-popover"
              >
                <div className="max-h-64 space-y-1 overflow-y-auto p-2">
                  {availableProjects.map((project) => {
                    const checked = selectedIds.has(project.id);
                    return (
                      <label
                        key={project.id}
                        className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        data-testid={`export-project-${project.id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleProject(project.id)}
                        />
                        <span className="flex-1 truncate">{project.name}</span>
                        {project.color && (
                          <span
                            aria-hidden
                            className="inline-block h-3 w-3 rounded-full border"
                            style={{ backgroundColor: project.color }}
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
                <div className="flex items-center justify-end gap-2 border-t px-2 py-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={selectAll}
                    disabled={selectedCount === totalCount}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={clearAll}
                    disabled={selectedCount === 0}
                  >
                    Clear
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 px-3 py-2 mt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPopoverOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleExport}
            disabled={
              entries.length === 0 ||
              (availableProjects.length > 0 && selectedCount === 0)
            }
            data-testid="export-excel-confirm"
          >
            <Download className="mr-2 h-4 w-4" />
            Download Excel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
